import React, { Component } from 'react';
import { pageEvents, processRegistrationPageLoad, processRegistration } from 'app/blocks/analytics/analytics';
import { addLoginValue, removeRegistrationValue } from 'app/blocks/analytics/storage';
import { Button } from 'app/blocks/buttons/buttons';
import ASInfo from 'app/blocks/common/as-info';
import { ID, withCodes } from 'app/blocks/common/codes';
import showDialog from 'app/blocks/common/jsx/dialogModal';
import Alerts from 'app/blocks/common/spinner';
import { ServiceError } from 'app/blocks/common/utils-errortypes';
import Layout from 'app/blocks/Layout/Layout';
import {
    addRedirectUriParam,
    getConnectRedirectUrl,
    getUiConnectUrl,
    securityKey,
} from 'app/blocks/middleware/authentication';
import * as RegisterMiddleware from 'app/blocks/middleware/register';
import { getUrlParam } from 'app/blocks/middleware/url-params';
import StaticContent from 'app/blocks/static-content/static-content';
import { SubTitle, MainTitle } from 'app/blocks/titles/titles';
import routes from 'app/pages/routes';
import RegistrationForm from './registration-form';
import RegistrationLoginMessage from './registration-login-message';
import SuccessfulMessage from './success-message';

import './registration.scss';

function isRegisteredAsPrimary(error: ServiceError): boolean {
    return error.code === 'REGISTRATION_INVITED_USER_ALREADY_REGISTERED';
}

function isRegisteredAsSecondary(error: ServiceError): boolean {
    return error.code === 'REGISTRATION_EMAIL_ALREADY_REGISTERED_AS_SECONDARY';
}

type State = {
    isLoading: boolean;
    invitation?: Awaited<ReturnType<typeof RegisterMiddleware.getInvitation>>;
    error?: ServiceError;
    registrationIsSuccessful: boolean;
    registeredEmail?: string;
    verificationRequired: boolean;
};

const getRedirectUrl = (url, redirectBaseUrl, invitation, invitationToken, articleId?) => {
    const urlObj = new URL(url);

    if (invitation) {
        urlObj.searchParams.set('firstname', invitation?.firstName || '');
        urlObj.searchParams.set('lastname', invitation?.lastName || '');
        urlObj.searchParams.set('emailaddress', invitation?.primaryEmailAddr || '');
        urlObj.searchParams.set('login_hint', invitation?.primaryEmailAddr || '');
        urlObj.searchParams.set('action', 'register');
        urlObj.searchParams.set('invitationToken', invitationToken || '');
    }

    const redirectPath = articleId ? `/index.html/article/${articleId}` : '/index.html/dashboard';
    addRedirectUriParam(urlObj, redirectPath, redirectBaseUrl, invitationToken);
    return securityKey(urlObj.href);
};

class RegistrationPage extends Component<{ articleId?: string; invitationToken?: string; l: l }, State> {
    state: State = {
        error: null,
        invitation: null,
        isLoading: !!this.props.invitationToken,
        registeredEmail: '',
        registrationIsSuccessful: false,
        verificationRequired: true,
    };

    async componentDidMount() {
        this.sendAnalytics();
        const invitation = await this.checkInviteInformation();
        const returnUrl = getUrlParam('returnUrl');

        if (!returnUrl) {
            this.goToRegistration(invitation);
        }
    }

    async goToRegistration(invitation) {
        try {
            Alerts.renderSpinner();
            const { articleId, invitationToken } = this.props;
            const url = await getUiConnectUrl('', true);
            const redirectBaseUrl = await getConnectRedirectUrl();
            window.location.href = getRedirectUrl(url, redirectBaseUrl, invitation, invitationToken, articleId);
        } catch (error) {
            Alerts.removeSpinner();
            showDialog.confirmation({
                confirmBtnLabel: this.props.l('BUTTONS.RELOAD_PAGE'),
                message: this.props.l('ERROR.OTHER_ERROR'),
                onApprove: () => this.goToRegistration(invitation),
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.invitationToken !== prevProps.invitationToken) {
            this.checkInviteInformation();
        }
    }

    sendAnalytics = () => {
        const data = { 'cta-region': removeRegistrationValue() || 'registration-link', step: 'registration initiated' };
        processRegistration(data, pageEvents.REGISTRATION_INITIATED);
        processRegistrationPageLoad(!!this.props.invitationToken, null, null);
    };

    checkInviteInformation = async () => {
        const { invitationToken } = this.props;
        this.setState({ error: null, invitation: null, isLoading: !!invitationToken });

        let invitation = null;

        try {
            if (invitationToken) {
                invitation = await RegisterMiddleware.getInvitation(invitationToken);
                this.setState({ invitation });
            }
        } catch (error) {
            if (isRegisteredAsPrimary(error) || isRegisteredAsSecondary(error)) {
                this.setState({ error });
            }
        } finally {
            this.setState({ isLoading: false });
        }

        return invitation;
    };

    onSuccessfulRegistration = (verificationRequired, registeredEmail) => {
        this.setState({
            registeredEmail,
            registrationIsSuccessful: true,
            verificationRequired,
        });
    };

    onClickLogin = () => {
        addLoginValue('registration-page');
        routes.toLogin();
    };

    renderInvitationBanner() {
        const { invitation } = this.state;
        const { l } = this.props;

        if (!invitation) return null;
        const inviters = (invitation.inviters || []).map(item => `${item.name} &lt;${item.email}&gt;`).join(', ');

        const message = l(`${ID.REGISTRATION}.inlineHelp.REGISTRATION_BY_INVITATION_BY_INVITERS_LOGIN_HELP`, {
            inviters,
        });
        return (
            <div className="InvitationBlock">
                <SubTitle data-seleniumid="page-alert-title">
                    <span>Invitation</span>
                </SubTitle>
                <p
                    className="InvitationBlock-Text"
                    dangerouslySetInnerHTML={{ __html: message }}
                    data-seleniumid="alert-message"
                />
                <div className="InvitationBlock-Actions">
                    <Button data-seleniumid="page-alert-btn" name="button" onClick={this.onClickLogin}>
                        {l(`${ID.REGISTRATION}.uiLabelMessages.REGISTRATION_LOGIN_BUTTON_LABEL`)}
                    </Button>
                </div>
            </div>
        );
    }

    renderContent() {
        const { error, invitation } = this.state;
        const { l } = this.props;

        if (error) {
            if (isRegisteredAsPrimary(error) || isRegisteredAsSecondary(error)) {
                return (
                    <RegistrationLoginMessage
                        existsAsPrimary={isRegisteredAsPrimary(error)}
                        existsAsSecondary={isRegisteredAsSecondary(error)}
                    />
                );
            }

            return <Layout error={error} />;
        }

        const { registeredEmail, registrationIsSuccessful, verificationRequired } = this.state;
        if (registrationIsSuccessful) {
            return verificationRequired ? (
                <SuccessfulMessage email={registeredEmail} />
            ) : (
                <RegistrationLoginMessage verificationNotRequired />
            );
        }

        return (
            <>
                {invitation && this.renderInvitationBanner()}
                <div className="RegisterPage-Container">
                    <div className="RegisterBlock">
                        <div className="RegisterBlock-FormContainer" data-seleniumid="register-block" id="inputdiv">
                            <RegistrationForm
                                invitation={invitation}
                                onSuccessfulRegistration={this.onSuccessfulRegistration}
                            />
                        </div>
                    </div>

                    <div className="OtherBlock" data-seleniumid="register-other-block">
                        <div className="LogIn Information" data-seleniumid="register-login-section">
                            <SubTitle className="LogIn-Title">
                                {l(`${ID.REGISTRATION}.uiLabelMessages.REGISTRATION_LOGIN_TO_WILEY_LABEL`)}
                            </SubTitle>
                            <div className="LogIn-Action">
                                <Button
                                    data-seleniumid="register-login-btn"
                                    id="register-login-btn"
                                    name="button"
                                    onClick={this.onClickLogin}
                                >
                                    {l(`${ID.REGISTRATION}.uiLabelMessages.REGISTRATION_LOGIN_BUTTON_LABEL`)}
                                </Button>
                            </div>
                        </div>
                        <div
                            className="Register Information"
                            data-seleniumid="register-help-section"
                            id="register-help-section"
                        >
                            <StaticContent key="register_help_section" src="register_help_section.html" />
                        </div>
                    </div>
                </div>
            </>
        );
    }

    render() {
        const { l } = this.props;
        const { isLoading } = this.state;
        const pageTitle = l(`${ID.REGISTRATION}.uiLabelMessages.REGISTRATION_REGISTER_LABEL`);
        const returnUrl = getUrlParam('returnUrl');

        return !returnUrl ? (
            <div>
                <Layout isLoading />
            </div>
        ) : (
            <main className="RegisterPage container-lg" id="as-main-content-7f3d9e2a">
                <MainTitle data-seleniumid="register-blockTitle">{pageTitle}</MainTitle>

                <Layout isLoading={isLoading}>{!isLoading && this.renderContent()}</Layout>
                <ASInfo page={pageTitle} />
            </main>
        );
    }
}

export default withCodes(RegistrationPage, ID.REGISTRATION);

export { getRedirectUrl };
